import { CheckOutlined, CloseOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  message,
  Modal,
  notification,
  Row,
  Select,
  Typography,
  Upload,
  UploadFile,
} from 'antd';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIQueries from '../../../api/useAPIQueries';
import Form from '../../../components/Form';
import {
  fileTypeToMimeType,
  supportedFiles,
} from '../../../components/InterviewScriptTemplateForm';
import RichEditor from '../../../components/RichEditor';
import { MAX_SIZE_2MB } from '../../../consts';
import useDiscardChangesModal from '../../../hooks/useDiscardChangesModal';
import { t as _t } from '../../../i18n';
import { SinapsePracticalTechnicalCaseResult } from '../../../sinapse/api';
import { ACTION_GENERATE_PRACTICAL_TECHNICAL_CASE } from '../../../sinapse/constants/actions';
import PracticalTechnicalCaseGenerator from '../../../sinapse/generate_practical_technical_case/PracticalTechnicalCaseGenerator';
import useAsyncSinapseResponse from '../../../sinapse/hooks/useAsyncSinapseResponse';
import {
  AttachmentDetail,
  ExpandedInterviewScriptTemplateCollection,
  JobPostingDetail,
} from '../../../types';
import { cleanQuillHtmlContent, markdownToQuillHtml } from '../../../utils';

interface PracticalTechnicalCaseFormFormProps {
  namePrefix?: (string | number)[];
  interviewScriptTemplates: ExpandedInterviewScriptTemplateCollection[];
  generatePracticalTechnicalCase?: boolean;
  isDescriptionEmpty?: boolean;
  setIsDirty: ReturnType<typeof useDiscardChangesModal>[0];
  jobPostingId?: JobPostingDetail['id'];
  isGeneratingInterviewScriptState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
}

const PracticalTechnicalCaseForm = ({
  namePrefix = [],
  interviewScriptTemplates,
  generatePracticalTechnicalCase,
  isDescriptionEmpty,
  jobPostingId,
  setIsDirty,
  isGeneratingInterviewScriptState,
}: PracticalTechnicalCaseFormFormProps) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance();

  const queryClient = useQueryClient();

  const lockInterviewScriptValue = Form.useWatch<boolean>(
    [...namePrefix, 'lock_interview_script'],
    form
  );
  const interviewScriptNameValue = Form.useWatch<string>(
    [...namePrefix, 'interview_script_name'],
    form
  );
  const interviewScriptTemplateValue = Form.useWatch<number>(
    [...namePrefix, 'interview_script_template'],
    form
  );
  const messageValue = Form.useWatch<string>([...namePrefix, 'message'], form);
  const interviewScriptContentValue = Form.useWatch<string>(
    [...namePrefix, 'interview_script_content'],
    form
  );
  const attachmentsValue = Form.useWatch<UploadFile[] | undefined>(
    [...namePrefix, 'attachments'],
    form
  );

  // Update interview script content with template value
  const changeInterviewScriptTemplate = useCallback(
    async (interviewScriptTemplateId: number) => {
      const template = interviewScriptTemplates?.find(
        (template) => template.id === interviewScriptTemplateId
      );

      if (!template) {
        form.setFieldValue(['stages', ...namePrefix, 'email_communication_template'], 0);
        return;
      }

      form.setFieldValue(['stages', ...namePrefix, 'interview_script_content'], template.content);

      const emailCommunicationTemplate = template.email_communication_template;

      if (!emailCommunicationTemplate) {
        form.setFieldValue(['stages', ...namePrefix, 'email_communication_template'], 0);
        form.setFieldValue(['stages', ...namePrefix, 'subject'], '');
        form.setFieldValue(['stages', ...namePrefix, 'fromName'], '');
        form.setFieldValue(['stages', ...namePrefix, 'message'], '');
        form.setFieldValue(['stages', ...namePrefix, 'attachments'], []);

        return;
      }

      const emailCommunicationTemplateAttachments: UploadFile[] = [];

      for (const attachmentId of emailCommunicationTemplate?.attachments || []) {
        const attachment = await queryClient.fetchQuery<AttachmentDetail>(
          `/attachments/${attachmentId}/`
        );
        emailCommunicationTemplateAttachments.push({
          uid: attachment.id.toString(),
          name: attachment.filename,
          status: 'done',
          url: attachment.file,
          type: attachment.mimetype,
        });
      }

      form.setFieldValue(
        ['stages', ...namePrefix, 'email_communication_template'],
        emailCommunicationTemplate.id
      );
      form.setFieldValue(['stages', ...namePrefix, 'subject'], emailCommunicationTemplate.subject);
      form.setFieldValue(
        ['stages', ...namePrefix, 'fromName'],
        emailCommunicationTemplate.from_name
      );
      form.setFieldValue(['stages', ...namePrefix, 'message'], emailCommunicationTemplate.message);
      form.setFieldValue(
        ['stages', ...namePrefix, 'attachments'],
        emailCommunicationTemplateAttachments
      );
    },
    [form, interviewScriptTemplates, namePrefix, queryClient]
  );

  const filteredAttachmentsValue = attachmentsValue?.filter((value) => !value.originFileObj);

  useAPIQueries(
    filteredAttachmentsValue?.map<[`/attachments/${string}/`]>((attachment) => [
      `/attachments/${attachment.uid}/`,
    ]) ?? [],
    {
      onSuccess: (attachments) => {
        if (attachments.length === 0) return;

        form.setFieldValue(
          ['stages', ...namePrefix, 'attachments'],
          attachmentsValue?.map((attachment) => {
            if (attachment.originFileObj) {
              return attachment;
            }

            const downloadedAttachment = attachments.find(
              (attach) => attach.id === parseInt(attachment.uid, 10)
            );

            if (downloadedAttachment) {
              return {
                uid: downloadedAttachment.id.toString(),
                name: downloadedAttachment.filename,
                status: 'done',
                url: downloadedAttachment.file,
                type: downloadedAttachment.mimetype,
              };
            }
            return attachment;
          })
        );
      },
      enabled: Boolean(filteredAttachmentsValue && filteredAttachmentsValue.length > 0),
    }
  );

  const [isGeneratingInterviewScript, setIsGeneratingInterviewScript] =
    isGeneratingInterviewScriptState;

  const { responseStatus, processResult } =
    useAsyncSinapseResponse<SinapsePracticalTechnicalCaseResult>({
      setIsDisabledInput: setIsGeneratingInterviewScript,
      setResult: (result) => {
        if ('err' in result) {
          return notification.error({
            message: t('Erro ao gerar as case prático ou técnico'),
            description: result.err,
          });
        }

        form.setFieldValue(['stages', ...namePrefix, 'interview_script_template'], 0);
        form.setFieldValue(['stages', ...namePrefix, 'email_communication_template'], 0);
        form.setFieldValue(
          ['stages', ...namePrefix, 'message'],
          markdownToQuillHtml(result.candidate_instructions)
        );
        form.setFieldValue(
          ['stages', ...namePrefix, 'interview_script_content'],
          markdownToQuillHtml(result.recruiter_instructions)
        );

        message.success(t('Case prático ou técnico gerado com sucesso'));
      },
      sourceId: jobPostingId,
      action: ACTION_GENERATE_PRACTICAL_TECHNICAL_CASE,
      configs: {
        overwriteOnSuccess: true,
        queryKeyPath: jobPostingId
          ? ['sinapse', 'screeningQuestions', jobPostingId, 'stage', ...namePrefix]
          : undefined,
      },
      enabled: generatePracticalTechnicalCase,
    });

  const loadingAIResponse = responseStatus === 'pending' && isGeneratingInterviewScript;

  const confirmPracticalTechnicalCaseOverride = useCallback(
    () =>
      Modal.confirm({
        title: _t('Sobrescrever o roteiro de case prático ou técnico?'),
        content: _t(
          'Ao gerar o roteiro de case prático ou técnico, as instruções para o avaliador e descrição para o candidato atuais serão substituídas. Você tem certeza que deseja prosseguir?'
        ),
        onOk: () => processResult?.(),
        okText: _t('Sim'),
        cancelText: _t('Não'),
        okButtonProps: {
          icon: <CheckOutlined />,
        },
        cancelButtonProps: { icon: <CloseOutlined />, danger: true },
      }),
    [processResult]
  );

  return (
    <Row gutter={[24, 8]}>
      <Col span={24}>
        <Typography.Title level={5} style={{ fontWeight: 'normal' }}>
          <Trans>Configurar case prático ou técnico</Trans>
        </Typography.Title>
        <Typography.Paragraph>
          <Trans>
            Você pode criar um novo case do zero ou colar um personalizado para utilizar durante
            essa etapa.
          </Trans>
        </Typography.Paragraph>
      </Col>
      {generatePracticalTechnicalCase && (
        <Col span={24}>
          <PracticalTechnicalCaseGenerator
            loading={loadingAIResponse}
            disabled={isDescriptionEmpty || lockInterviewScriptValue}
            disabledMessage={
              isDescriptionEmpty ? t('Preencha a descrição da vaga para gerar as perguntas') : ''
            }
            onGenerateClick={() => {
              setIsDirty(true);

              if (
                cleanQuillHtmlContent(messageValue) &&
                cleanQuillHtmlContent(interviewScriptContentValue)
              ) {
                return confirmPracticalTechnicalCaseOverride();
              }

              processResult?.();
            }}
          />
        </Col>
      )}
      {lockInterviewScriptValue ? (
        <>
          <Col span={24}>
            <Card>
              <Form.Item label={t('Roteiro de case prático ou técnico')}>
                <Select
                  disabled
                  value={interviewScriptNameValue}
                  options={[
                    {
                      value: interviewScriptNameValue,
                      key: interviewScriptNameValue,
                    },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label={t('Instruções para o avaliador')}
                name={[...namePrefix, 'interview_script_content']}
              >
                <RichEditor readOnly image={false} video={false} />
              </Form.Item>
              <Form.Item label={t('Descrição para o candidato')} name={[...namePrefix, 'message']}>
                <RichEditor key={`rce-stage-${namePrefix}`} readOnly image video={false} />
              </Form.Item>
              <Form.Item
                name={[...namePrefix, 'attachments']}
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e && e.fileList;
                }}
              >
                <Upload data-testid="upload-attachments" listType="text" disabled>
                  <Button icon={<UploadOutlined />} disabled>
                    Upload
                  </Button>
                </Upload>
              </Form.Item>
            </Card>
          </Col>
        </>
      ) : (
        <>
          <Col span={24}>
            <Card>
              <Form.Item
                label={t('Roteiro de case prático ou técnico')}
                name={[...namePrefix, 'interview_script_template']}
              >
                <Select
                  placeholder={t('Escolha o roteiro')}
                  allowClear
                  onChange={changeInterviewScriptTemplate}
                  disabled={loadingAIResponse}
                  options={[
                    {
                      key: `personalizado`,
                      value: 0,
                      label: t('Personalizado'),
                    },
                    ...(interviewScriptTemplates
                      ?.filter(
                        (interviewScriptTemplate) =>
                          interviewScriptTemplate.type.includes('PRACTICAL_TECHNICAL_CASE') ||
                          interviewScriptTemplate.id === interviewScriptTemplateValue
                      )
                      .map((interviewScriptTemplate) => ({
                        value: interviewScriptTemplate.id,
                        key: interviewScriptTemplate.id,
                        label: interviewScriptTemplate.name,
                      })) || {}),
                  ]}
                />
              </Form.Item>
              <Form.Item
                label={t('Instruções para o avaliador')}
                name={[...namePrefix, 'interview_script_content']}
              >
                <RichEditor
                  key={`rce-stage-${namePrefix}`}
                  image={false}
                  video={false}
                  readOnly={loadingAIResponse}
                  onChange={(value: string) => {
                    const template = interviewScriptTemplates?.find(
                      (template) => template.id === interviewScriptTemplateValue
                    );

                    if (!template) {
                      return;
                    }

                    if (template.content !== value) {
                      form.setFieldValue(['stages', ...namePrefix, 'interview_script_template'], 0);
                      form.setFieldValue(
                        ['stages', ...namePrefix, 'email_communication_template'],
                        0
                      );
                    }
                  }}
                />
              </Form.Item>
              <Form.Item label={t('Descrição para o candidato')} name={[...namePrefix, 'message']}>
                <RichEditor
                  key={`rce-stage-${namePrefix}`}
                  image
                  video={false}
                  readOnly={loadingAIResponse}
                  onChange={(value: string) => {
                    if (interviewScriptTemplateValue === 0) {
                      return;
                    }

                    const template = interviewScriptTemplates?.find(
                      (template) => template.id === interviewScriptTemplateValue
                    );

                    if (!template) {
                      return;
                    }

                    if (
                      !template.email_communication_template ||
                      template.email_communication_template.message !== value
                    ) {
                      form.setFieldValue(['stages', ...namePrefix, 'interview_script_template'], 0);
                      form.setFieldValue(
                        ['stages', ...namePrefix, 'email_communication_template'],
                        0
                      );
                    }
                  }}
                  variables={[
                    { value: '[candidato]', label: t('Nome do Candidato') },
                    { value: '[empresa]', label: t('Nome da Empresa') },
                    { value: '[vaga]', label: t('Nome da Vaga') },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name={[...namePrefix, 'attachments']}
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e && e.fileList;
                }}
              >
                <Upload
                  data-testid="upload-attachments"
                  listType="text"
                  accept={supportedFiles.join(',')}
                  beforeUpload={(file: UploadFile) => {
                    if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                      if (!file.size) {
                        message.error(t('Erro com o arquivo.'));
                        return;
                      }
                      if (file.size >= MAX_SIZE_2MB) {
                        message.error(
                          t(
                            'O arquivo selecionado excede o limite de 2 MB. Por favor, envie um arquivo menor.'
                          )
                        );
                        return Upload.LIST_IGNORE;
                      }
                      return false;
                    } else {
                      message.error(
                        t('Por favor, selecione um arquivo que é aceito pelo sistema.')
                      );
                      return Upload.LIST_IGNORE;
                    }
                  }}
                  onChange={() => {
                    if (interviewScriptTemplateValue === 0) {
                      return;
                    }

                    form.setFieldValue(['stages', ...namePrefix, 'interview_script_template'], 0);
                    form.setFieldValue(
                      ['stages', ...namePrefix, 'email_communication_template'],
                      0
                    );
                  }}
                >
                  <Button disabled={loadingAIResponse} icon={<UploadOutlined />}>
                    Upload
                  </Button>
                </Upload>
              </Form.Item>
            </Card>
          </Col>
        </>
      )}
    </Row>
  );
};

export default PracticalTechnicalCaseForm;
